import ChevronDownIcon from '@src/svgs/ChevronDownIcon';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled, { css, keyframes } from 'styled-components';

import { useAnchoredDropdown } from './useAnchoredDropdown';

import type { FC, ReactNode } from 'react';

/**
 * Split button — a primary action with variant choices (design-system.md →
 * "Split Button"). The main segment fires the default action; the attached
 * chevron segment opens a menu of variants. Two real <button> segments give
 * separate hit targets, keyboard activation, and menu ARIA for free.
 */
export type SplitButtonItem = {
  key: string;
  label: string;
  onSelect: () => void;
  disabled?: boolean;
  /** Right-aligned muted tag, e.g. "Default" */
  note?: string;
  /** Optional leading glyph, matching the main segment's `icon`. */
  icon?: ReactNode;
};

type Props = {
  /** Main segment label, e.g. "Publish" */
  label: string;
  /** Fired by the main segment */
  onClick: () => void;
  /** The menu's rows. May be empty when `menu` renders the menu's content instead. */
  items: SplitButtonItem[];
  /**
   * PLAT-12079 — render the menu's content yourself (the audio tag picker's
   * panel) in place of the rows; `close` shuts the menu. The portal, the
   * placement and the dismissal stay the button's.
   */
  menu?: (close: () => void) => ReactNode;
  /** Attributes set on the menu surface (a host's portal marker, a test id). */
  menuAttrs?: Record<string, string>;
  /** The menu's z-index when the default tier is not enough (above a popover). */
  menuZIndex?: string | number;
  disabled?: boolean;
  /** Collapses to a single non-interactive pill: spinner + busyLabel */
  busy?: boolean;
  busyLabel?: string;
  /** Error/retry variant: --destructive fill, still clickable */
  destructive?: boolean;
  /**
   * PLAT-11514 — outlined treatment instead of a fill, for a surface that
   * deliberately has no primary action (the Translation Review header). Same
   * anatomy and menu; only the skin changes. Ignored when `destructive`.
   */
  variant?: 'primary' | 'secondary';
  /** Optional leading glyph in the main segment (e.g. a link icon). */
  icon?: ReactNode;
  /**
   * The WIDEST label this button will ever show. When `label` swaps for
   * transient feedback ("Review Link" -> "Link Copied") the segment would
   * otherwise resize and shove everything beside it; declaring the widest
   * string reserves that width up front. Self-maintaining — no magic pixel
   * value to re-measure when the copy changes.
   */
  widestLabel?: string;
  /** aria-label for the chevron segment, e.g. "Publish options" */
  menuAriaLabel: string;
  /**
   * Menu panel width. A number pins it; `'trigger'` matches the button's own
   * width, which suits short labels that would otherwise float in a panel
   * far wider than they need. Defaults to 200 (the resolution menu's width).
   */
  menuWidth?: number | 'trigger';
};

// `width: undefined` makes `useAnchoredDropdown` fall back to the trigger's own
// width, so `menuWidth: 'trigger'` needs no special-casing downstream.
const menuOptionsFor = (
  menuWidth: number | 'trigger',
  zIndex?: string | number,
): Parameters<typeof useAnchoredDropdown>[4] => ({
  width: menuWidth === 'trigger' ? undefined : menuWidth,
  align: 'end',
  placement: 'below',
  ...(zIndex !== undefined ? { zIndex } : {}),
});

const SplitButton: FC<Props> = ({
  label,
  onClick,
  items,
  menu,
  menuAttrs,
  menuZIndex,
  disabled,
  busy,
  busyLabel,
  destructive,
  variant = 'primary',
  icon,
  widestLabel,
  menuAriaLabel,
  menuWidth = 200,
}) => {
  const [open, setOpen] = useState(false);
  // The trigger ref covers BOTH segments: useAnchoredDropdown exempts the
  // trigger from its outside-close, and `align: 'end'` must measure the whole
  // control so the menu's right edge lines up with the button's.
  const triggerRef = useRef<HTMLDivElement>(null);
  const { menuRef, style } = useAnchoredDropdown(
    open && !busy,
    triggerRef,
    () => setOpen(false),
    undefined,
    menuOptionsFor(menuWidth, menuZIndex),
  );

  useEffect(() => {
    if (busy || disabled) setOpen(false);
  }, [busy, disabled]);

  if (busy) {
    return (
      <BusyPill $destructive={destructive} aria-busy="true">
        <Spinner />
        {busyLabel}
      </BusyPill>
    );
  }

  return (
    <>
      <Root ref={triggerRef} $disabled={disabled}>
        <MainSegment
          type="button"
          $destructive={destructive}
          $outlined={variant === 'secondary' && !destructive}
          disabled={disabled}
          onClick={() => {
            setOpen(false);
            onClick();
          }}
        >
          {icon && <IconSlot>{icon}</IconSlot>}
          <LabelStack>
            <span>{label}</span>
            {widestLabel && (
              <LabelSizer aria-hidden="true">{widestLabel}</LabelSizer>
            )}
          </LabelStack>
        </MainSegment>
        <ChevronSegment
          type="button"
          $destructive={destructive}
          $outlined={variant === 'secondary' && !destructive}
          disabled={disabled}
          aria-haspopup="menu"
          aria-expanded={open}
          aria-label={menuAriaLabel}
          onClick={() => setOpen(o => !o)}
        >
          <ChevronWrap $open={open}>
            <ChevronDownIcon strokeColor="currentColor" />
          </ChevronWrap>
        </ChevronSegment>
      </Root>
      {open &&
        !busy &&
        style &&
        createPortal(
          <Menu
            ref={menuRef}
            style={style}
            role={menu ? undefined : 'menu'}
            {...menuAttrs}
          >
            {menu?.(() => setOpen(false))}
            {items.map(item => (
              <MenuItem
                key={item.key}
                type="button"
                role="menuitem"
                disabled={item.disabled}
                onClick={() => {
                  setOpen(false);
                  item.onSelect();
                }}
              >
                <MenuItemLabel>
                  {item.icon}
                  <span>{item.label}</span>
                </MenuItemLabel>
                {item.note && <Note>{item.note}</Note>}
              </MenuItem>
            ))}
          </Menu>,
          document.body,
        )}
    </>
  );
};

export default SplitButton;

const segmentFill = css<{ $destructive?: boolean }>`
  color: var(--background);
  background: ${({ $destructive }) =>
    $destructive ? 'var(--destructive)' : 'var(--primary)'};
  font-family: inherit;
  font-size: var(--text-xs);
  font-weight: var(--font-weight-semi-bold);
`;

const Root = styled.div<{ $disabled?: boolean }>`
  display: inline-flex;
  align-items: stretch;
  height: var(--btn-h-sm);

  ${({ $disabled }) =>
    $disabled &&
    css`
      opacity: 0.5;
      pointer-events: none;
    `}
`;

// PLAT-11514 — the outlined skin. Design-system Secondary tier: transparent,
// neutral border, foreground text. The two segments share ONE divider because
// the main segment drops its right border; giving both a full border would
// paint a 2px seam.
const segmentOutline = css`
  color: var(--foreground);
  background: transparent;
  border: 1px solid var(--border);
  font-family: inherit;
  font-size: var(--text-xs);
  font-weight: var(--font-weight-medium);
`;

const SegmentBase = styled.button<{
  $destructive?: boolean;
  $outlined?: boolean;
}>`
  ${({ $outlined }) => ($outlined ? segmentOutline : segmentFill)}
  display: flex;
  align-items: center;
  ${({ $outlined }) => !$outlined && 'border: none;'}
  cursor: pointer;
  transition:
    background var(--transition-fast),
    border-color var(--transition-fast);

  &:hover:not(:disabled) {
    ${({ $destructive, $outlined }) =>
      $outlined
        ? css`
            background: var(--surface-elevated);
            border-color: var(--text-muted);
          `
        : $destructive
          ? css`
              filter: brightness(1.08);
            `
          : css`
              background: var(--primary-hover);
            `}
  }

  &:disabled {
    cursor: default;
  }
`;

// A fixed box for the glyph so swapping icons (a 16px link for a 14px check)
// cannot nudge the label by a couple of pixels.
const IconSlot = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  flex-shrink: 0;
`;

// The visible label and the hidden widest-label sizer share one grid cell, so
// the stack is always as wide as the LONGEST of the two and the segment never
// resizes when the label swaps.
const LabelStack = styled.span`
  display: grid;
  min-width: 0;

  > * {
    grid-area: 1 / 1;
    white-space: nowrap;
  }
`;

const LabelSizer = styled.span`
  visibility: hidden;
  pointer-events: none;
`;

const MainSegment = styled(SegmentBase)`
  gap: 6px;
  padding: 0 var(--btn-px-sm);
  border-radius: var(--radius-button) 0 0 var(--radius-button);
  // Outlined: drop the right edge so the chevron's left border is the only
  // divider between the two segments.
  ${({ $outlined }) => $outlined && 'border-right: none;'}

  svg {
    flex-shrink: 0;
  }
`;

const ChevronSegment = styled(SegmentBase)`
  width: 26px;
  flex-shrink: 0;
  justify-content: center;
  border-radius: 0 var(--radius-button) var(--radius-button) 0;
  border-left: 1px solid
    ${({ $outlined }) =>
      $outlined
        ? 'var(--border)'
        : 'color-mix(in srgb, var(--background) 35%, transparent)'};
`;

const ChevronWrap = styled.span<{ $open: boolean }>`
  display: flex;
  transition: transform var(--transition-fast);
  transform: rotate(${({ $open }) => ($open ? '180deg' : '0deg')});
`;

const BusyPill = styled.div<{ $destructive?: boolean }>`
  ${segmentFill}
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  height: var(--btn-h-sm);
  min-width: 64px;
  padding: 0 var(--btn-px-sm);
  border-radius: var(--radius-button);
`;

const spin = keyframes`
  to {
    transform: rotate(360deg);
  }
`;

const Spinner = styled.span`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  border: 1.5px solid color-mix(in srgb, currentColor 30%, transparent);
  border-top-color: currentColor;
  animation: ${spin} 0.8s linear infinite;
`;

// Chrome only — position, z-index, width, and max-height come from
// useAnchoredDropdown's inline style.
// The menu is portaled to <body>. Its z-index is owned by
// `useAnchoredDropdown`, which sets it INLINE (and follows the top-layer lift),
// so declaring one here would be dead CSS — inline always wins.
const Menu = styled.div`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: var(--spacing-xs);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  background: var(--background);
  box-shadow: var(--elevation-toast);
  overflow-y: auto;
`;

// Leading glyph + label travel together so the right-aligned `note` keeps its
// own edge (the row is space-between).
const MenuItemLabel = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-width: 0;

  svg {
    flex-shrink: 0;
  }
`;

const MenuItem = styled.button`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  width: 100%;
  min-height: 32px;
  padding: 0 10px;
  border: none;
  border-radius: 6px;
  background: none;
  text-align: left;
  cursor: pointer;
  color: var(--text-secondary);
  font-family: inherit;
  font-size: var(--text-xs);
  font-weight: var(--font-weight-normal);

  &:hover:not(:disabled) {
    background: var(--surface-elevated);
    color: var(--foreground);
  }

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

const Note = styled.span`
  font-size: var(--text-xs);
  color: var(--text-muted);
`;
